import React, { useState } from 'react';
import {
  AritoIcon,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ConfirmationDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { GiaMuaProvider, useGiaMuaContext } from './context';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { ActionBar, FormDialog } from './components';
import type { GiaMuaInput } from '@/types/schemas';
import { ExcelConfig, ImportBody } from './utils';

function GiaMuaPageContent() {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();

  const {
    // Search & Table
    handleSearchChange,
    tables,
    selectedRowIndex,
    handleRowClick,
    handleRowDoubleClick,
    currentPage,
    totalItems,
    handlePageChange,
    isLoading,
    searchState,

    // Form state
    showForm,
    showDelete,
    selectedObj,
    formMode,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    clearSelection,

    // CRUD operations
    addItem,
    updateItem,
    deleteItem,
    refreshData,

    // Import state
    isImportDialogOpen,
    openImportDialog,
    closeImportDialog,
    isLoadingImport,
    setIsLoadingImport
  } = useGiaMuaContext();

  const handleFormSubmit = async (data: GiaMuaInput) => {
    try {
      if (formMode === 'add') {
        await addItem(data);
      } else if (formMode === 'edit' && selectedObj && selectedObj.uuid) {
        await updateItem(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
      await refreshData();
    } catch (error) {
      return;
    }
  };

  const handleImportBodies = async (bodies: ImportBody[]) => {
    let successCount = 0;
    setIsLoadingImport(true);
    try {
      for (const body of bodies) {
        await addItem(body);
        successCount++;
      }
      toast({
        description: 'Đã nhập danh mục giá mua từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      await refreshData();
      setIsLoadingImport(false);
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  return (
    <div className='flex h-screen flex-col overflow-y-scroll'>
      <div className='w-full'>
        <ActionBar
          onAddClick={handleAddClick}
          onEditClick={() => selectedObj && handleEditClick()}
          onDeleteClick={() => selectedObj && handleDeleteClick()}
          onCopyClick={() => selectedObj && handleCopyClick()}
          onViewClick={() => selectedObj && handleViewClick()}
          onRefreshClick={() => refreshData()}
          onImportFromExcel={openImportDialog}
          hasRowSelected={!!selectedObj}
        />

        <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
          <ErpDataTableV2
            tables={tables}
            selectedRowId={selectedRowIndex || undefined}
            onRowClick={handleRowClick}
            onRowDoubleClick={handleRowDoubleClick}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSearchChange={handleSearchChange}
            pageSize={20}
            loading={isLoading || searchState.isSearching}
          />
        </div>
      </div>

      {showForm && (
        <FormDialog
          open={showForm}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : selectedObj}
          onAddButtonClick={() => {
            handleCloseForm();
            clearSelection();
            handleAddClick();
          }}
          onEditButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDeleteButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopyButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      <ConfirmationDialog
        open={showDelete}
        onClose={handleCloseDelete}
        onConfirm={async () => {
          await deleteItem(selectedObj!.uuid);
          await refreshData();
          handleCloseDelete();
          clearSelection();
        }}
      />

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={closeImportDialog}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}

export default function DanhMucGiaMua() {
  return (
    <GiaMuaProvider>
      <GiaMuaPageContent />
    </GiaMuaProvider>
  );
}
